// modules/freshcards/src/core/overlays/resolver.ts
//
// 2026-09-18 (PROMPT F 19 Phase 3a) — Cascade resolver.
// Walks FacePreference storage in surface > project > account > default order
// (most-specific wins), validates FreshCards version compatibility, applies
// shallow overrides, returns the active AppOverlay.
//
// DESIGN (operator approved 2026-09-18 09:00 Europe/Paris):
//   - Cache TTL: 1s per scope triple, invalidated on apply/revert.
//   - Compatibility: warn + fallback to default (not hard-error).
//   - Overrides: shallow merge (theme/layout/views full-replace per field).
//   - DEFAULT_OVERLAY_ID: 'overlay.freshcards.default' (Phase 7 wires actual).
//   - Cascade order: surface > project > account > default.

import type { AppOverlay, OverlayCompatibility } from '../../types/models/AppOverlay';
import {
  loadFacePreference,
  type FacePreference,
  type FacePreferenceStorage,
  type FaceScope,
} from './facePreference';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Stable id of the default overlay. Phase 7 wires the real one. */
export const DEFAULT_OVERLAY_ID = 'overlay.freshcards.default';

/** Cache TTL in milliseconds. */
export const RESOLVER_CACHE_TTL_MS = 1_000;

/**
 * FreshCards version this resolver was authored for.
 * TODO: source from package.json at build time. Hardcoded until then.
 */
export const FRESHCARDS_VERSION = '1.0.0';

// ---------------------------------------------------------------------------
// Semver (inline `satisfies()` — no full semver lib per F 19 design #4)
// ---------------------------------------------------------------------------

/**
 * Tiny semver comparator supporting:
 *   - exact       "1.2.3"
 *   - caret       "^1.2.3"    → >=1.2.3 <2.0.0
 *   - tilde       "~1.2.3"    → >=1.2.3 <1.3.0
 *   - gte         ">=1.2.3"
 *   - gt          ">1.2.3"
 *   - lte         "<=1.2.3"
 *   - lt          "<1.2.3"
 *   - any         "*"
 *
 * For multi-range constraints ("A B"), pass the first valid expression
 * the caller cares about. The compatibility check uses single-range
 * strings (semver convention for FvW §00.12 compatibility constraints).
 *
 * Pre-release tags are not handled (FreshCards is GA-style semver).
 */
export function semverSatisfies(version: string, range: string): boolean {
  const v = parseSemver(version);
  const r = parseSemverRange(range);
  if (!v || !r) return false;
  return cmpGte(v, r.min) && (r.max === undefined || cmpLt(v, r.max));
}

interface ParsedSemver {
  major: number;
  minor: number;
  patch: number;
}

interface ParsedRange {
  min: ParsedSemver;
  max?: ParsedSemver; // exclusive upper bound (only for caret/tilde)
  op: 'exact' | 'caret' | 'tilde' | 'gte' | 'gt' | 'lte' | 'lt' | 'any';
}

function parseSemver(s: string): ParsedSemver | null {
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(s.trim());
  if (!m) return null;
  return { major: +m[1], minor: +m[2], patch: +m[3] };
}

function parseSemverRange(r: string): ParsedRange | null {
  const s = r.trim();
  if (s === '*' || s === '') return { min: { major: 0, minor: 0, patch: 0 }, op: 'any' };
  const caret = /^\^(\d+)\.(\d+)\.(\d+)$/.exec(s);
  if (caret) {
    const min = { major: +caret[1], minor: +caret[2], patch: +caret[3] };
    const max = min.major === 0 ? { major: 0, minor: min.minor + 1, patch: 0 } : { major: min.major + 1, minor: 0, patch: 0 };
    return { min, max, op: 'caret' };
  }
  const tilde = /^~(\d+)\.(\d+)\.(\d+)$/.exec(s);
  if (tilde) {
    const min = { major: +tilde[1], minor: +tilde[2], patch: +tilde[3] };
    return { min, max: { major: min.major, minor: min.minor + 1, patch: 0 }, op: 'tilde' };
  }
  const gte = /^>=(\d+)\.(\d+)\.(\d+)$/.exec(s);
  if (gte) return { min: { major: +gte[1], minor: +gte[2], patch: +gte[3] }, op: 'gte' };
  const lte = /^<=(\d+)\.(\d+)\.(\d+)$/.exec(s);
  if (lte) {
    const max = { major: +lte[1], minor: +lte[2], patch: +lte[3] };
    return { min: { major: 0, minor: 0, patch: 0 }, max: { major: max.major + 1, minor: 0, patch: 0 }, op: 'lt' };
  }
  const gt = /^>(\d+)\.(\d+)\.(\d+)$/.exec(s);
  if (gt) return { min: { major: +gt[1], minor: +gt[2], patch: +gt[3] }, op: 'gt' };
  const lt = /^<(\d+)\.(\d+)\.(\d+)$/.exec(s);
  if (lt) return { min: { major: 0, minor: 0, patch: 0 }, max: { major: +lt[1], minor: +lt[2], patch: +lt[3] }, op: 'lt' };
  const exact = /^(\d+)\.(\d+)\.(\d+)$/.exec(s);
  if (exact) return { min: { major: +exact[1], minor: +exact[2], patch: +exact[3] }, op: 'exact' };
  return null;
}

function cmpGte(a: ParsedSemver, b: ParsedSemver): boolean {
  if (a.major !== b.major) return a.major > b.major;
  if (a.minor !== b.minor) return a.minor > b.minor;
  return a.patch >= b.patch;
}

function cmpLt(a: ParsedSemver, b: ParsedSemver): boolean {
  if (a.major !== b.major) return a.major < b.major;
  if (a.minor !== b.minor) return a.minor < b.minor;
  return a.patch < b.patch;
}

// ---------------------------------------------------------------------------
// Resolver types
// ---------------------------------------------------------------------------

/** Source of the resolved overlay — for debugging + diagnostics. */
export type OverlaySource = 'surface' | 'project' | 'account' | 'default' | 'fallback';

/** Resolved overlay + provenance. */
export interface ResolvedOverlay {
  overlay: AppOverlay;
  source: OverlaySource;
  /** Scope the resolution came from (undefined for 'default'/'fallback'). */
  scope?: FaceScope;
  /** True if compatibility was checked AND passed. */
  compatible: boolean;
  /** Warning message if compatibility fell back to default. */
  warning?: string;
}

/** Result type for resolver — matches StorageResult style. */
export type ResolveResult =
  | { ok: true; resolved: ResolvedOverlay }
  | { ok: false; error: string };

/** Overlay lookup function — pluggable so the registry can be injected. */
export type OverlayLookup = (id: string) => AppOverlay | null;

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

interface CacheEntry {
  result: ResolveResult;
  cachedAt: number;
}

const _cache: Map<string, CacheEntry> = new Map();

/** Cache key: user|project|surface (undefined → '~'). */
export function cacheKey(scope: FaceScope): string {
  return `${scope.userId ?? '~'}|${scope.projectId ?? '~'}|${scope.surfaceId ?? '~'}`;
}

/** Drop a single cache entry (or all). */
export function clearResolverCache(scope?: FaceScope): void {
  if (!scope) {
    _cache.clear();
    return;
  }
  _cache.delete(cacheKey(scope));
}

// ---------------------------------------------------------------------------
// Cascade walk
// ---------------------------------------------------------------------------

/**
 * Build the cascade list of scopes from most-specific to least.
 * Each scope is a "drop one level" — surface first, then project, then account.
 */
export function cascadeScopes(scope: FaceScope): FaceScope[] {
  const list: FaceScope[] = [];
  if (scope.surfaceId !== undefined) {
    list.push({ userId: scope.userId, projectId: scope.projectId, surfaceId: scope.surfaceId });
  }
  if (scope.projectId !== undefined) {
    list.push({ userId: scope.userId, projectId: scope.projectId });
  }
  if (scope.userId !== undefined) {
    list.push({ userId: scope.userId });
  }
  return list;
}

/**
 * Find the first non-null FacePreference walking surface > project > account.
 * Returns {pref, scope, source} on hit, null if no preference at any level.
 */
export async function findCascadeHit(
  storage: FacePreferenceStorage,
  scope: FaceScope,
): Promise<{ pref: FacePreference; scope: FaceScope; source: OverlaySource } | null> {
  const scopes = cascadeScopes(scope);
  for (const s of scopes) {
    const pref = await loadFacePreference(storage, s);
    if (pref && typeof pref.overlayId === 'string' && pref.overlayId.length > 0) {
      const source: OverlaySource =
        s.surfaceId !== undefined ? 'surface' : s.projectId !== undefined ? 'project' : 'account';
      return { pref, scope: s, source };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Compatibility check (warn + fallback per design #2)
// ---------------------------------------------------------------------------

function checkCompatibility(overlay: AppOverlay): { ok: boolean; warning?: string } {
  const c: OverlayCompatibility = overlay.compatibility;
  if (!c?.freshcards) {
    return { ok: false, warning: `Overlay ${overlay.id} has no compatibility.freshcards` };
  }
  if (semverSatisfies(FRESHCARDS_VERSION, c.freshcards)) return { ok: true };
  return {
    ok: false,
    warning: `Overlay ${overlay.id} requires FreshCards ${c.freshcards}, have ${FRESHCARDS_VERSION}`,
  };
}

// ---------------------------------------------------------------------------
// Shallow override merge (per design #3)
// ---------------------------------------------------------------------------

/**
 * Apply user overrides on top of an overlay. Shallow at the top level:
 * theme/layout/views are replaced wholesale (not deep-merged). If the user
 * wants partial theme tweaks, they should author a new overlay.
 */
export function applyOverrides(base: AppOverlay, overrides?: Partial<AppOverlay>): AppOverlay {
  if (!overrides) return base;
  return {
    ...base,
    ...overrides,
    id: base.id, // id never changes
    compatibility: overrides.compatibility ?? base.compatibility,
    bundleHash: overrides.bundleHash ?? base.bundleHash,
  };
}

// ---------------------------------------------------------------------------
// Resolver (main entry)
// ---------------------------------------------------------------------------

/**
 * Resolve the active overlay for a scope. Walks surface > project > account
 * > default, caches result for 1s, applies overrides, validates compatibility
 * (warn + fallback).
 */
export async function resolveOverlay(
  storage: FacePreferenceStorage,
  lookup: OverlayLookup,
  scope: FaceScope,
  now: number = Date.now(),
): Promise<ResolveResult> {
  const key = cacheKey(scope);
  const cached = _cache.get(key);
  if (cached && now - cached.cachedAt < RESOLVER_CACHE_TTL_MS) {
    return cached.result;
  }

  const result = await resolveOverlayNoCache(storage, lookup, scope);
  _cache.set(key, { result, cachedAt: now });
  return result;
}

async function resolveOverlayNoCache(
  storage: FacePreferenceStorage,
  lookup: OverlayLookup,
  scope: FaceScope,
): Promise<ResolveResult> {
  // Try cascade hit first.
  const hit = await findCascadeHit(storage, scope);

  if (!hit) {
    // No preference anywhere → default.
    const def = lookup(DEFAULT_OVERLAY_ID);
    if (!def) {
      // No default registered yet (Phase 7 will register). Return ok with
      // an empty fallback so the UI can render without an overlay.
      return {
        ok: true,
        resolved: {
          overlay: emptyOverlay(),
          source: 'default',
          compatible: true,
        },
      };
    }
    return { ok: true, resolved: { overlay: def, source: 'default', compatible: true } };
  }

  // We have a preference. Look up the overlay.
  const overlay = lookup(hit.pref.overlayId);
  if (!overlay) {
    // Preference references an overlay not registered. Fall back to default.
    const def = lookup(DEFAULT_OVERLAY_ID);
    return {
      ok: true,
      resolved: {
        overlay: def ?? emptyOverlay(),
        source: 'fallback',
        scope: hit.scope,
        compatible: false,
        warning: `Preferred overlay ${hit.pref.overlayId} not registered; falling back to default`,
      },
    };
  }

  // Check compatibility (warn + fallback per design #2).
  const compat = checkCompatibility(overlay);
  if (!compat.ok) {
    const def = lookup(DEFAULT_OVERLAY_ID);
    return {
      ok: true,
      resolved: {
        overlay: def ?? emptyOverlay(),
        source: 'fallback',
        scope: hit.scope,
        compatible: false,
        warning: compat.warning,
      },
    };
  }

  // Apply overrides (shallow merge per design #3).
  const final = applyOverrides(overlay, hit.pref.overrides);

  return {
    ok: true,
    resolved: {
      overlay: final,
      source: hit.source,
      scope: hit.scope,
      compatible: true,
    },
  };
}

/** Empty overlay used as last-resort fallback when no default is registered. */
function emptyOverlay(): AppOverlay {
  return {
    id: 'overlay.freshcards.empty',
    name: 'Empty',
    version: '0.0.0',
    flavor: 'bundle',
    distribution: 'generic',
    author: { id: 'system', displayName: 'System' },
    license: { spdx: 'proprietary' },
    compatibility: { freshcards: '>=0.0.0' },
    bundleHash: '0000000000000000000000000000000000000000000000000000000000000000',
  };
}

// ---------------------------------------------------------------------------
// Test exports (kept here so the resolver is self-contained)
// ---------------------------------------------------------------------------

/** @internal Exported for tests only. */
export const _internal = {
  parseSemver,
  parseSemverRange,
  cmpGte,
  cmpLt,
  emptyOverlay,
};
